import React, { FunctionComponent, Fragment, useContext, useCallback, useEffect } from "react";
import { Grid, makeStyles, Theme, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { LoginFormButton } from "./form-components/login-form-button";
import { LoginFormTextField } from "./form-components/login-form-textfield";
import { LoginContext } from "./context/login-context";
import { LOGIN_FORM_TYPE } from "../../models/login-form-type";
import { useChangePasswordMutation } from "../../api/smartmelts-api/graphql/generated";
import { SnackbarSeverity, useSnackbar } from "../../components/snackbar/snackbar-context";
import { getGraphqlErrorLocalized } from "../../api/errors/graphql-error-handler";
import { usePassword } from "../../hooks/use-password";
import { useHistory } from "react-router";
import { useGraphQL } from "../../api/context/graphql-context";
import { useEnterCallback } from "../../hooks/use-enter-callback";

const useStyles = makeStyles((theme: Theme) => ({
  heading: {
    color: theme.palette.text.primary,
    padding: theme.spacing(2),
  },
}));

interface IPasswordChangeFormProps {}

export const PasswordChangeForm: FunctionComponent<IPasswordChangeFormProps> = (props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { showSnackbar } = useSnackbar();
  const { setAuthorizationHeader } = useGraphQL();
  const { setFormType, toggleShakeAnimation } = useContext(LoginContext);
  const { password, isPasswordValid, onPasswordChange } = usePassword("");
  const {
    password: passwordRepeat,
    isPasswordValid: isPasswordRepeatValid,
    onPasswordChange: onPasswordRepeatChange,
    isPasswordSame,
  } = usePassword("", password);
  const history = useHistory();

  useEffect(() => history.replace({ search: "" }), [history]);

  const customOnPasswordChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => onPasswordChange(event.target.value),
    [onPasswordChange],
  );
  const customOnPasswordRepeatChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => onPasswordRepeatChange(event.target.value),
    [onPasswordRepeatChange],
  );

  const [changePasswordMutation, { loading }] = useChangePasswordMutation({
    onCompleted: (data) => {
      setAuthorizationHeader(data.changePassword.token);
      setFormType(LOGIN_FORM_TYPE.CODE);
      showSnackbar(t("login.change_password_info"));
    },
    onError: (error) => {
      showSnackbar(getGraphqlErrorLocalized(t, "login", error), SnackbarSeverity.ERROR);
    },
  });

  const onClickReset = useCallback(() => {
    if (isPasswordValid && isPasswordRepeatValid && isPasswordSame) {
      changePasswordMutation({ variables: { password } });
    } else {
      toggleShakeAnimation();
    }
  }, [changePasswordMutation, isPasswordSame, isPasswordValid, isPasswordRepeatValid, password, toggleShakeAnimation]);

  const onKeyDown = useEnterCallback(onClickReset);

  return (
    <Fragment>
      <Grid container direction="column">
        <Grid item className={classes.heading}>
          <Typography align="center">{t("login.change_password")}</Typography>
        </Grid>
        <LoginFormTextField
          label={t("login.fields.password")}
          type="password"
          value={password}
          onChange={customOnPasswordChange}
          error={password.length !== 0 && !isPasswordValid}
          errorMessage={t("login.errors.password_too_short")}
          onKeyDown={onKeyDown}
        />
        <LoginFormTextField
          label={t("login.fields.repeat_password")}
          type="password"
          value={passwordRepeat}
          onChange={customOnPasswordRepeatChange}
          error={passwordRepeat.length !== 0 && (!isPasswordRepeatValid || !isPasswordSame)}
          errorMessage={!isPasswordSame ? t("login.errors.passwords_dont_match") : t("login.errors.password_too_short")}
          onKeyDown={onKeyDown}
        />
      </Grid>
      <Grid container justify="center">
        <LoginFormButton onClick={onClickReset} loading={loading}>
          {t("login.change_password_action")}
        </LoginFormButton>
      </Grid>
    </Fragment>
  );
};
